/**
 * The two directions between a wall clock and an instant (piece 2).
 *
 * A calendar field reads and writes a wall clock — `2026-09-14T09:00`, which
 * is nine in the morning wherever the event is held — while every stored
 * timestamp is UTC and every record with a physical place carries an IANA zone
 * name. 🔨 The store refuses a local time with no zone, and it is right to:
 * the conversion happens here, at the boundary, where the zone is in hand.
 *
 * 💰 An hour that daylight saving skips has no instant, and an hour it repeats
 * has two. This resolves the first forward, past the gap, and the second to
 * the earlier of the pair. A calendar cannot ask the question a second time.
 */

const DAY_SHAPE = /^\d{4}-\d{2}-\d{2}$/;
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;

export class ZonedTimeError extends Error {}

/**
 * 🔒 One `Intl.DateTimeFormat` per zone and option set, module-level and never
 * cleared (T533). Constructing a formatter is the expensive half of a
 * conversion, and every helper below is called per row of a list or per cell
 * of a calendar grid. A bad zone still throws — the constructor runs before
 * the cache is written, so a failed construction is never cached — and the
 * same options always key the same cached instance, so two callers of
 * `offsetMs` never build two formatters for one zone.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function optionsKey(options: Intl.DateTimeFormatOptions): string {
  return Object.keys(options)
    .sort()
    .map((key) => `${key}=${String((options as Record<string, unknown>)[key])}`)
    .join(',');
}

/** The cached formatter for a zone and an option set, built once and reused (T533). */
export function cachedFormatter(zone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const cacheKey = `${zone}|${optionsKey(options)}`;
  const found = formatterCache.get(cacheKey);
  if (found) return found;
  const formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone });
  formatterCache.set(cacheKey, formatter);
  return formatter;
}

/**
 * How far ahead of UTC a zone's clock reads at one instant, in milliseconds.
 * Formatting the instant in the zone and reading the parts back as if they
 * were UTC gives the shifted clock; the difference is the offset.
 */
function offsetMs(instant: Date, zone: string): number {
  const parts = cachedFormatter(zone, {
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const at = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');
  const shifted = Date.UTC(at('year'), at('month') - 1, at('day'), at('hour') % 24, at('minute'), at('second'));
  return shifted - Math.floor(instant.getTime() / 1000) * 1000;
}

/**
 * The instant a wall clock names in a zone. The offset is itself a function of
 * the instant, so the naive reading is corrected once and the correction is
 * checked: two passes settle every case a fixed offset settles, and the pair
 * of daylight-saving hours is resolved by the rule above.
 */
export function instantFromWallClock(wall: string, zone: string): Date {
  const fields = WALL_CLOCK.exec(wall.trim());
  if (!fields) throw new ZonedTimeError(`a wall clock reads YYYY-MM-DDTHH:MM; got '${wall}'`);
  const [, year, month, day, hour, minute, second] = fields;
  const naive = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second ?? '0'));
  if (Number.isNaN(naive)) throw new ZonedTimeError(`not a valid wall clock: '${wall}'`);

  const naiveGuess = naive - offsetMs(new Date(naive), zone);
  const corrected = naive - offsetMs(new Date(naiveGuess), zone);
  if (naiveGuess === corrected) return new Date(naiveGuess);

  // The two readings disagree, so the wall clock sits on a transition. The
  // earlier instant is the answer wherever it reads back as the clock asked
  // for; otherwise the hour does not exist and the later one is taken.
  const earlier = Math.min(naiveGuess, corrected);
  const later = Math.max(naiveGuess, corrected);
  return new Date(readsBackAs(earlier, zone, naive) ? earlier : later);
}

/**
 * The zone's abbreviation at an instant, EST or EDT, as a person writes it
 * after a clock; a zone with no common abbreviation reads as its offset,
 * GMT+9. The instant matters: the same zone abbreviates differently in
 * summer and winter.
 */
export function zoneAbbreviation(instant: Date | string, zone: string): string {
  const at = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(at.getTime())) throw new ZonedTimeError(`not a valid instant: '${String(instant)}'`);
  const parts = cachedFormatter(zone, { timeZoneName: 'short' }).formatToParts(at);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? zone;
}

/**
 * 🔒 The zone's offset from UTC at an instant, as a short label: UTC−5,
 * UTC+5:30, or UTC where the clocks agree (piece 15; T575). The instant
 * matters, because daylight saving moves the offset.
 */
export function zoneOffsetLabel(instant: Date | string, zone: string): string {
  const at = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(at.getTime())) throw new ZonedTimeError(`not a valid instant: '${String(instant)}'`);
  const minutes = Math.round(offsetMs(at, zone) / 60_000);
  if (minutes === 0) return 'UTC';
  const whole = Math.abs(minutes);
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  return `UTC${minutes < 0 ? '\u2212' : '+'}${hours}${rest === 0 ? '' : `:${String(rest).padStart(2, '0')}`}`;
}

/**
 * The clock an instant reads in a zone with the zone named after it, the way
 * a check-in reads on the Site page: 5:00 PM EST (product owner, 2026-09-12).
 * 🔨 A stored instant is never printed raw; it is drawn in a zone, and the
 * zone is named beside it so a reader elsewhere is not misled.
 */
export function clockInZone(instant: Date | string, zone: string): string {
  const at = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(at.getTime())) throw new ZonedTimeError(`not a valid instant: '${String(instant)}'`);
  const time = cachedFormatter(zone, { hour: 'numeric', minute: '2-digit' }).format(at);
  return `${time} ${zoneAbbreviation(at, zone)}`;
}

/**
 * The calendar day an instant falls on in a zone, as `YYYY-MM-DD`. 🔨 Every
 * boundary cut off a calendar — a year, a quarter, a month — is cut off this
 * rather than off `getUTCFullYear` (found in review, 2026-09-15). A job running
 * at 02:00 UTC on 1 January runs at 16:00 on 31 December in Hawaii, and a UTC
 * reading moves the boundary a day for everyone west of Greenwich.
 */
export function calendarDayInZone(instant: Date | string, zone: string): string {
  return wallClockInZone(instant, zone).slice(0, 10);
}

/** The same day split into the three numbers a `Date.UTC` boundary is built from. */
export function calendarPartsInZone(instant: Date | string, zone: string): { year: number; monthIndex: number; day: number } {
  const [year, month, day] = calendarDayInZone(instant, zone).split('-').map(Number) as [number, number, number];
  return { year, monthIndex: month - 1, day };
}

/** True when an instant's clock in a zone is the wall clock that was asked for. */
function readsBackAs(instant: number, zone: string, naive: number): boolean {
  return instant + offsetMs(new Date(instant), zone) === naive;
}

/**
 * The wall clock an instant reads in a zone, as a `datetime-local` field wants
 * it. 💰 Slicing a UTC string instead shows the event an hour or six from
 * where it is held, which is the bug the stored zone exists to prevent.
 */
export function wallClockInZone(instant: Date | string, zone: string): string {
  const at = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(at.getTime())) throw new ZonedTimeError(`not a valid instant: '${String(instant)}'`);
  const shifted = new Date(at.getTime() + offsetMs(at, zone));
  return shifted.toISOString().slice(0, 16);
}

/**
 * The wall clock `minutes` later than a wall clock in a zone, counted as
 * elapsed time so a daylight-saving change moves the clock and not the length
 * (piece 29, T1710: a job type's length is the booking window's default end).
 * Null where the wall clock is not one, such as a half-typed field.
 */
export function wallClockAfterMinutes(wall: string, minutes: number, zone: string): string | null {
  if (!WALL_CLOCK.test(wall.trim())) return null;
  return wallClockInZone(new Date(instantFromWallClock(wall, zone).getTime() + minutes * 60_000), zone);
}

/** Milliseconds in a day, for arithmetic on instants where daylight saving does not matter (T678: the one constant). */
export const DAY_MS = 86_400_000;

/** A whole number written at least `width` digits wide, zero-filled: the month 7 as `07`, the year 926 as `0926` (T678). */
export function zeroPad(value: number, width = 2): string {
  return String(value).padStart(width, '0');
}

/** How many days a month has; `monthIndex` is zero-based and may run past the year (T678). */
export function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/**
 * A real calendar day, `YYYY-MM-DD` and nothing else, so 2026-02-30 is refused
 * rather than rolled into March (T665). 🔨 The one check every module uses; no
 * file writes its own regex and round trip.
 */
export function isCalendarDay(value: unknown): value is string {
  if (typeof value !== 'string' || !DAY_SHAPE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/**
 * True for text written `YYYY-MM-DD`, whether or not that day exists (T665). 🔨 Only for a caller that tells a bare day
 * from a timestamp or from a typo and never asks whether the day is real; a caller that asks uses `isCalendarDay`.
 */
export function hasDayShape(value: string): boolean {
  return DAY_SHAPE.test(value);
}

/**
 * A calendar day, `days` later or earlier than `day`, by calendar arithmetic
 * on `YYYY-MM-DD` alone (T532). 🔨 No zone enters here: once an instant has
 * been cut to a day by `calendarDayInZone`, moving that day is arithmetic on
 * the three numbers, never a second reading of a clock.
 */
export function addCalendarDays(day: string, days: number): string {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(year, (month ?? 1) - 1, (date ?? 1) + days)).toISOString().slice(0, 10);
}

/**
 * 🔒 The last instant of a calendar day in a zone: one millisecond before the
 * next day's midnight there (T915). A walk that reads "everything on or
 * before this day" reads to here, so a receipt stamped with the time of day
 * counts on the day it was made, wherever the organisation keeps its clock.
 */
export function endOfDayInZone(day: string, zone: string): Date {
  return new Date(instantFromWallClock(`${addCalendarDays(day, 1)}T00:00`, zone).getTime() - 1);
}

/** The UTC start of the day an instant or a stored `@db.Date` falls on; a date column is read and written in UTC (T947). */
export function utcDayStart(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

/**
 * The start, as the database's Date column holds it, of the calendar day an instant falls on in the organization's zone.
 * The in-force rule reads this day, so a row dated by `utcDayStart` between 00:00 and 05:00 UTC in America/Chicago starts
 * a day ahead and is not yet in force (found while building, 2026-10-11).
 */
export function organizationDayStart(instant: Date, zone: string): Date {
  return new Date(`${calendarDayInZone(instant, zone)}T00:00:00Z`);
}

/** A calendar date as the database's Date column holds it, `YYYY-MM-DD`; text already a day is cut to its first ten characters (T947). */
export function isoDay(value: string | Date): string {
  return typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10);
}

/** A stored `@db.Date` back to its `YYYY-MM-DD`; a missing date stays missing (T947). */
export function dayText(date: Date): string;
export function dayText(date: Date | null | undefined): string | null;
export function dayText(date: Date | null | undefined): string | null {
  return date ? isoDay(date) : null;
}
